/**
 * Event handler utilities for camera controls
 * Handles mouse, touch, keyboard, and wheel events
 */

/**
 * Apply momentum scrolling animation
 * @param {Object} component - Component instance
 */
const applyMomentum = (component) => {
  const { momentum, camera } = component.state;
  
  // Friction coefficient (0.95 = smooth deceleration)
  const friction = 0.92;
  const minVelocity = 0.1; // Stop when velocity is very small
  
  // Apply velocity to camera position
  const newCamera = {
    ...camera,
    x: camera.x + momentum.vx,
    y: camera.y + momentum.vy
  };
  
  // Apply friction to velocity
  const newMomentum = {
    vx: momentum.vx * friction,
    vy: momentum.vy * friction
  };
  
  // Check if we should stop (velocity too low)
  if (Math.abs(newMomentum.vx) < minVelocity && Math.abs(newMomentum.vy) < minVelocity) {
    component.setState({ momentum: { vx: 0, vy: 0 } });
    component.momentumAnimation = null;
    
    // Final visibility update
    const { updateVisibleNodes } = require('./cameraUtils');
    updateVisibleNodes(component);
    return;
  }
  
  // Update state and continue animation
  component.setState({
    camera: newCamera,
    momentum: newMomentum
  });
  
  component.momentumAnimation = requestAnimationFrame(() => applyMomentum(component));
};

/**
 * Create event handlers for a camera component instance
 * @param {Object} component - The component instance (with state, setState, etc.)
 * @returns {Object} Object containing all event handler functions
 */
export const createEventHandlers = (component) => {
  // Throttle timers for performance
  let wheelThrottle = null;
  let visibilityUpdateThrottle = null;

  return {
    handleMouseDown(e) {
      if (e.button === 0) {
        // Cancel any ongoing momentum
        if (component.momentumAnimation) {
          cancelAnimationFrame(component.momentumAnimation);
          component.momentumAnimation = null;
        }
        
        component.setState({
          isPanning: true,
          lastPanPoint: { x: e.clientX, y: e.clientY },
          navigationCollapsed: true,  // Auto-collapse navigation when clicking on tree
          momentum: { vx: 0, vy: 0 },
          lastMoveTime: Date.now(),
          panHistory: []
        });
        e.preventDefault();
      }
    },

    handleMouseMove(e) {
      const { isPanning, lastPanPoint, camera, panHistory } = component.state;

      if (isPanning) {
        const now = Date.now();
        const deltaX = e.clientX - lastPanPoint.x;
        const deltaY = e.clientY - lastPanPoint.y;

        // Track movement for momentum calculation
        const newPanHistory = [...panHistory, { deltaX, deltaY, time: now }];
        // Keep only last 5 movements for velocity calculation
        if (newPanHistory.length > 5) {
          newPanHistory.shift();
        }

        component.setState({
          camera: {
            ...camera,
            x: camera.x + deltaX,
            y: camera.y + deltaY
          },
          lastPanPoint: { x: e.clientX, y: e.clientY },
          lastMoveTime: now,
          panHistory: newPanHistory
        });

        // Throttle visibility updates during panning
        if (!visibilityUpdateThrottle) {
          visibilityUpdateThrottle = setTimeout(() => {
            const { updateVisibleNodes } = require('./cameraUtils');
            updateVisibleNodes(component);
            visibilityUpdateThrottle = null;
          }, 50);
        }
      }
    },

    handleMouseUp(e) {
      if (e.button === 0) {
        const { panHistory } = component.state;
        
        // Calculate velocity from recent movements
        let vx = 0;
        let vy = 0;
        
        if (panHistory.length > 0) {
          // Average the last few movements for smoother velocity
          const recentMoves = panHistory.slice(-3);
          vx = recentMoves.reduce((sum, move) => sum + move.deltaX, 0) / recentMoves.length;
          vy = recentMoves.reduce((sum, move) => sum + move.deltaY, 0) / recentMoves.length;
          
          // Amplify velocity for more noticeable momentum (adjust multiplier for feel)
          vx *= 1.5;
          vy *= 1.5;
        }
        
        component.setState({ 
          isPanning: false,
          momentum: { vx, vy },
          panHistory: []
        });
        
        // Start momentum animation if velocity is significant
        if (Math.abs(vx) > 0.5 || Math.abs(vy) > 0.5) {
          if (component.momentumAnimation) {
            cancelAnimationFrame(component.momentumAnimation);
          }
          component.momentumAnimation = requestAnimationFrame(() => applyMomentum(component));
        } else {
          // Force visibility update when panning stops (no momentum)
          const { updateVisibleNodes } = require('./cameraUtils');
          updateVisibleNodes(component);
        }
      }
    },

    handleTouchStart(e) {
      // Scrollable overlays (search results…) handle their own touch scrolling
      if (e.target.closest && e.target.closest('[data-camera-scrollable]')) return;

      if (e.touches.length === 1) {
        // Cancel any ongoing momentum
        if (component.momentumAnimation) {
          cancelAnimationFrame(component.momentumAnimation);
          component.momentumAnimation = null;
        }
        
        const touch = e.touches[0];
        component.setState({
          isPanning: true,
          lastPanPoint: { x: touch.clientX, y: touch.clientY },
          navigationCollapsed: true,  // Auto-collapse navigation when touching tree
          momentum: { vx: 0, vy: 0 },
          lastMoveTime: Date.now(),
          panHistory: []
        });
      } else if (e.touches.length === 2) {
        e.preventDefault();
        
        // Cancel any ongoing momentum immediately when pinch zooming
        if (component.momentumAnimation) {
          cancelAnimationFrame(component.momentumAnimation);
          component.momentumAnimation = null;
        }
        
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;

        component.setState({
          isPanning: false,
          touchStartDistance: distance,
          lastTouchCenter: { x: centerX, y: centerY },
          momentum: { vx: 0, vy: 0 }
        });
      }
    },

    handleTouchMove(e) {
      // Scrollable overlays (search results…) handle their own touch scrolling
      if (e.target.closest && e.target.closest('[data-camera-scrollable]')) return;

      const { isPanning, lastPanPoint, camera, touchStartDistance, lastTouchCenter, minZoomLevel, panHistory } = component.state;

      if (e.touches.length === 1 && isPanning) {
        // Stop iOS Safari from scrolling/rubber-banding the page while panning
        e.preventDefault();
        const touch = e.touches[0];
        const now = Date.now();
        const deltaX = touch.clientX - lastPanPoint.x;
        const deltaY = touch.clientY - lastPanPoint.y;

        // Track movement for momentum calculation
        const newPanHistory = [...panHistory, { deltaX, deltaY, time: now }];
        // Keep only last 5 movements for velocity calculation
        if (newPanHistory.length > 5) {
          newPanHistory.shift();
        }

        component.setState({
          camera: {
            ...camera,
            x: camera.x + deltaX,
            y: camera.y + deltaY
          },
          lastPanPoint: { x: touch.clientX, y: touch.clientY },
          lastMoveTime: now,
          panHistory: newPanHistory
        });
      } else if (e.touches.length === 2 && touchStartDistance) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;

        const scaleChange = currentDistance / touchStartDistance;
        // Use calculated minZoomLevel as the minimum, with 3 as maximum
        const newScale = Math.max(minZoomLevel || 0.1, Math.min(3, camera.scale * scaleChange));

        const container = component.containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();

        const zoomPointX = centerX - rect.left;
        const zoomPointY = centerY - rect.top;

        const scaleRatio = newScale / camera.scale;
        const newX = zoomPointX - (zoomPointX - camera.x) * scaleRatio;
        const newY = zoomPointY - (zoomPointY - camera.y) * scaleRatio;

        const panDeltaX = centerX - lastTouchCenter.x;
        const panDeltaY = centerY - lastTouchCenter.y;

        component.setState({
          camera: {
            x: newX + panDeltaX,
            y: newY + panDeltaY,
            scale: newScale
          },
          touchStartDistance: currentDistance,
          lastTouchCenter: { x: centerX, y: centerY },
          isActivelyZooming: true,
          zoomSettling: true
        });

        // Reset zoom idle timeout - switch to crisp rendering after pinch stops
        if (component.zoomIdleTimeout) {
          clearTimeout(component.zoomIdleTimeout);
        }
        if (component.zoomSettleTimeout) {
          clearTimeout(component.zoomSettleTimeout);
        }
        component.zoomIdleTimeout = setTimeout(() => {
          component.setState({ isActivelyZooming: false });
          // Re-enable node transitions only after the render mode has switched
          // back to CSS zoom, so the zoom-induced computed left/top jump on
          // WebKit is applied instantly instead of animating
          component.zoomSettleTimeout = setTimeout(() => {
            component.setState({ zoomSettling: false });
          }, 50);
        }, 150);
      }
    },

    handleTouchEnd(e) {
      if (e.touches.length === 0) {
        const { panHistory } = component.state;
        
        // Calculate velocity from recent movements
        let vx = 0;
        let vy = 0;
        
        if (panHistory.length > 0) {
          // Average the last few movements for smoother velocity
          const recentMoves = panHistory.slice(-3);
          vx = recentMoves.reduce((sum, move) => sum + move.deltaX, 0) / recentMoves.length;
          vy = recentMoves.reduce((sum, move) => sum + move.deltaY, 0) / recentMoves.length;
          
          // Amplify velocity for mobile (more momentum on touch)
          vx *= 2.0;
          vy *= 2.0;
        }
        
        component.setState({
          isPanning: false,
          touchStartDistance: null,
          momentum: { vx, vy },
          panHistory: []
        });
        
        // Start momentum animation if velocity is significant
        if (Math.abs(vx) > 0.5 || Math.abs(vy) > 0.5) {
          if (component.momentumAnimation) {
            cancelAnimationFrame(component.momentumAnimation);
          }
          component.momentumAnimation = requestAnimationFrame(() => applyMomentum(component));
        } else {
          // Force visibility update when touch ends (no momentum)
          const { updateVisibleNodes } = require('./cameraUtils');
          updateVisibleNodes(component);
        }
      } else if (e.touches.length === 1) {
        const touch = e.touches[0];
        component.setState({
          isPanning: true,
          lastPanPoint: { x: touch.clientX, y: touch.clientY },
          touchStartDistance: null,
          panHistory: []
        });
      }
    },

    handleWheel(e) {
      // Scrollable overlays (search results…) scroll themselves instead of zooming
      if (e.target.closest && e.target.closest('[data-camera-scrollable]')) return;

      e.preventDefault();

      // Cancel any ongoing momentum immediately when zooming
      if (component.momentumAnimation) {
        cancelAnimationFrame(component.momentumAnimation);
        component.momentumAnimation = null;
      }
      component.setState({ momentum: { vx: 0, vy: 0 }, isActivelyZooming: true, zoomSettling: true });

      const { camera, minZoomLevel } = component.state;
      const container = component.containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const scaleFactor = e.deltaY > 0 ? 0.9 : 1.1;
      // Use calculated minZoomLevel as the minimum, with 3 as maximum
      const newScale = Math.max(minZoomLevel || 0.1, Math.min(3, camera.scale * scaleFactor));

      const scaleChange = newScale / camera.scale;
      const newX = mouseX - (mouseX - camera.x) * scaleChange;
      const newY = mouseY - (mouseY - camera.y) * scaleChange;

      component.setState({
        camera: {
          x: newX,
          y: newY,
          scale: newScale
        }
      });

      // Throttle visibility updates during wheel zoom
      if (wheelThrottle) {
        clearTimeout(wheelThrottle);
      }
      wheelThrottle = setTimeout(() => {
        const { updateVisibleNodes } = require('./cameraUtils');
        updateVisibleNodes(component);
        wheelThrottle = null;
      }, 100);

      // Reset zoom idle timeout - switch to crisp rendering after zoom stops
      if (component.zoomIdleTimeout) {
        clearTimeout(component.zoomIdleTimeout);
      }
      if (component.zoomSettleTimeout) {
        clearTimeout(component.zoomSettleTimeout);
      }
      component.zoomIdleTimeout = setTimeout(() => {
        component.setState({ isActivelyZooming: false });
        // See pinch handler: keep node transitions suspended through the
        // zoom/transform render mode switch
        component.zoomSettleTimeout = setTimeout(() => {
          component.setState({ zoomSettling: false });
        }, 50);
      }, 150);
    },

    handleKeyDown(e) {
      // e.key is undefined on events that aren't real key presses (browser autofill,
      // synthetic events dispatched by extensions), which threw on .toLowerCase()
      if (typeof e.key !== 'string') return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        component.toggleSearch();
        return;
      }

      if (e.key === 'Escape') {
        component.setState({ showSearch: false, searchQuery: '', searchResults: [] });
        return;
      }

      component.keys.add(e.key.toLowerCase());
    },

    handleKeyUp(e) {
      if (typeof e.key !== 'string') return;
      component.keys.delete(e.key.toLowerCase());
    }
  };
};
